/**
 * 中央ツールバー共通部品（主CTA・アイコンボタン・サンプル出典・他のサンプル）
 */

import { useEffect, useRef } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  getStandardDatasetById,
  type StandardDatasetEntry,
} from '../../../../services/systems/keisuke/keisukeStandardDatasets';
import KeisukeSampleDataMenuItems from './KeisukeSampleDataMenuItems';
import styles from './KeisukeDashboardCenterPane.module.scss';

/** 読込直後に summary を短く目立たせる（本文は開かず作業域を守る） */
const SAMPLE_INFO_HINT_PULSE_MS = 2400;

/** 空／途中入力で共通の緑「サンプルで試す」 */
export function KeisukePrimarySampleButton({
  title,
  onClick,
  className,
}: {
  title: string;
  onClick: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <Button
      size="sm"
      variant="success"
      className={className}
      onClick={onClick}
      title={title}
    >
      {t('keisuke.workspace.emptyHeroTrySample')}
    </Button>
  );
}

type ToolbarIconButtonProps = {
  icon: string;
  label?: string;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  /** アイコンのみ（aria-label は label または title を使う） */
  iconOnly?: boolean;
  ariaLabel?: string;
};

export function ToolbarIconButton({
  icon,
  label,
  title,
  onClick,
  disabled,
  iconOnly = false,
  ariaLabel,
}: ToolbarIconButtonProps) {
  return (
    <Button
      size="sm"
      variant="outline-secondary"
      className={
        iconOnly
          ? `${styles.toolbarIconBtn} ${styles.toolbarIconOnly}`
          : styles.toolbarIconBtn
      }
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel ?? label ?? title}
    >
      <i className={`fas ${icon}`} aria-hidden />
      {!iconOnly && label ? (
        <span className={styles.toolbarBtnLabel}>{label}</span>
      ) : null}
    </Button>
  );
}

export function KeisukeSampleInfoDetails({ sampleId }: { sampleId: string }) {
  const { t } = useTranslation();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const entry = getStandardDatasetById(sampleId);

  /**
   * 既定は閉じたまま。読込時だけ summary を短くパルスして存在を示す。
   * （自動展開はツールバー全幅帯になり表作業を圧迫するため廃止）
   */
  useEffect(() => {
    const el = detailsRef.current;
    if (!el) return;
    el.open = false;
    el.dataset.hintPulse = '1';
    const timer = window.setTimeout(() => {
      delete el.dataset.hintPulse;
    }, SAMPLE_INFO_HINT_PULSE_MS);
    return () => {
      window.clearTimeout(timer);
      delete el.dataset.hintPulse;
    };
  }, [sampleId]);

  if (!entry || (!entry.referenceKey && !entry.referenceUrl && !entry.learningPointKey)) {
    return null;
  }

  const summaryTitle = [
    t('keisuke.dashboard.sampleDataInfo'),
    entry.learningPointKey ? t(entry.learningPointKey) : null,
  ]
    .filter(Boolean)
    .join(' — ');

  return (
    <details
      ref={detailsRef}
      className={`${styles.sampleInfo} ${styles.sampleInfoCompact}`}
    >
      <summary className={styles.sampleInfoSummary} title={summaryTitle}>
        {t('keisuke.dashboard.sampleDataInfoSummary')}
      </summary>
      {/*
        role=region を details に付けない（ネイティブ disclosure を上書きし、
        閉じていても本文が a11y ツリーに残る）。本文は 1 ノードにまとめる。
      */}
      <div className={styles.sampleInfoBody}>
        {(entry.referenceKey || entry.referenceUrl) && (
          <p className={styles.sampleInfoRef}>
            <strong>{t('keisuke.dashboard.sampleDataReference')}:</strong>{' '}
            {entry.referenceUrl ? (
              <a
                href={entry.referenceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.sampleInfoLink}
              >
                {entry.referenceKey ? t(entry.referenceKey) : entry.referenceUrl}
              </a>
            ) : (
              t(entry.referenceKey!)
            )}
          </p>
        )}
        {entry.learningPointKey && (
          <p className={styles.sampleInfoPoint}>
            <strong>{t('keisuke.dashboard.sampleDataLearningPoint')}:</strong>{' '}
            {t(entry.learningPointKey)}
          </p>
        )}
      </div>
    </details>
  );
}

export type KeisukeSampleDataDropdownProps = {
  tutorialForType: StandardDatasetEntry[];
  verificationForType: StandardDatasetEntry[];
  hasAnySample: boolean;
  onLoadSampleData: (entry: StandardDatasetEntry) => void;
  excludeSampleId?: string | null;
  id: string;
  align?: 'start' | 'end';
  /** outline-secondary = ツールバー、link = 空ヒーロー */
  variant?: 'outline-secondary' | 'link';
  toggleClassName?: string;
};

/** 空ヒーロー／充填後ツールバー共通の「他のサンプル」ドロップダウン */
export function KeisukeSampleDataDropdown({
  tutorialForType,
  verificationForType,
  hasAnySample,
  onLoadSampleData,
  excludeSampleId,
  id,
  align = 'end',
  variant = 'outline-secondary',
  toggleClassName,
}: KeisukeSampleDataDropdownProps) {
  const { t } = useTranslation();
  return (
    <Dropdown className="d-inline" align={align}>
      <Dropdown.Toggle
        size="sm"
        variant={variant}
        className={toggleClassName}
        id={id}
        disabled={!hasAnySample}
        title={t('keisuke.dashboard.tooltips.sampleData')}
      >
        <i className="fas fa-flask" aria-hidden />
        {variant === 'link' ? (
          t('keisuke.workspace.emptyHeroOtherSamples')
        ) : (
          <span className={styles.toolbarBtnLabel}>
            {t('keisuke.workspace.emptyHeroOtherSamples')}
          </span>
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <KeisukeSampleDataMenuItems
          tutorialForType={tutorialForType}
          verificationForType={verificationForType}
          hasAnySample={hasAnySample}
          onLoadSampleData={onLoadSampleData}
          excludeSampleId={excludeSampleId}
        />
      </Dropdown.Menu>
    </Dropdown>
  );
}
